/**
 * FM db-service — Dine-In: sectores, mesas y sesiones de mesa.
 * Fase 11.3 — dine_in_tables
 */
import { sql, nextId } from "../db.js";
import { resolveDefaultLocationId } from "../inventory-engine.js";

// ─── Tipos ───────────────────────────────────────────────────────────────────

export type TableStatus = "LIBRE" | "OCUPADA" | "PIDIENDO" | "PAGO_PENDIENTE";
export type TableSessionStatus = "open" | "closed" | "cancelled";

export interface Sector {
  id: string;
  tenantId: string;
  locationId: string | null;
  nombre: string;
  orden: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DineTable {
  id: string;
  tenantId: string;
  sectorId: string | null;
  nombre: string;
  capacidad: number;
  estado: TableStatus;
  qrToken: string;
  posX: number;
  posY: number;
  active: boolean;
  /** Si false, no suma cupo a reservas online. */
  reservable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TableSession {
  id: string;
  tenantId: string;
  tableId: string;
  openedBy: string;
  openedAt: string;
  closedAt: string | null;
  closedBy: string | null;
  status: TableSessionStatus;
  totalAcumulado: number;
  comensales: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Row mappers ─────────────────────────────────────────────────────────────

function rowToSector(r: Record<string, unknown>): Sector {
  return {
    id: String(r.id),
    tenantId: String(r.tenant_id),
    locationId: (r.location_id as string | null) ?? null,
    nombre: String(r.nombre),
    orden: Number(r.orden),
    active: Boolean(r.active),
    createdAt: String(r.created_at),
    updatedAt: String(r.updated_at),
  };
}

function rowToTable(r: Record<string, unknown>): DineTable {
  return {
    id: String(r.id),
    tenantId: String(r.tenant_id),
    sectorId: (r.sector_id as string | null) ?? null,
    nombre: String(r.nombre),
    capacidad: Number(r.capacidad),
    estado: (r.estado as TableStatus) ?? "LIBRE",
    qrToken: String(r.qr_token),
    posX: Number(r.pos_x ?? 0),
    posY: Number(r.pos_y ?? 0),
    active: Boolean(r.active),
    reservable: r.reservable === undefined ? true : Boolean(r.reservable),
    createdAt: String(r.created_at),
    updatedAt: String(r.updated_at),
  };
}

function rowToSession(r: Record<string, unknown>): TableSession {
  return {
    id: String(r.id),
    tenantId: String(r.tenant_id),
    tableId: String(r.table_id),
    openedBy: String(r.opened_by),
    openedAt: String(r.opened_at),
    closedAt: (r.closed_at as string | null) ?? null,
    closedBy: (r.closed_by as string | null) ?? null,
    status: (r.status as TableSessionStatus) ?? "open",
    totalAcumulado: Number(r.total_acumulado),
    comensales: Number(r.comensales ?? 1),
    notes: (r.notes as string | null) ?? null,
    createdAt: String(r.created_at),
    updatedAt: String(r.updated_at),
  };
}

// ─── Sectores ─────────────────────────────────────────────────────────────────

export async function dbSmListSectors(tenantId: string, locationId?: string | null): Promise<Sector[]> {
  const db = sql();
  const loc = locationId ?? null;
  const rows =
    await db`SELECT * FROM sm_sectors WHERE tenant_id = ${tenantId} AND active = true
             AND (${loc}::text IS NULL OR location_id = ${loc})
             ORDER BY orden, nombre`;
  return rows.map(rowToSector);
}

export async function dbSmGetSector(tenantId: string, id: string): Promise<Sector | null> {
  const db = sql();
  const rows = await db`SELECT * FROM sm_sectors WHERE tenant_id = ${tenantId} AND id = ${id} LIMIT 1`;
  return rows[0] ? rowToSector(rows[0]) : null;
}

export async function dbSmCreateSector(
  tenantId: string,
  input: { nombre: string; locationId?: string | null; orden?: number },
): Promise<Sector> {
  const db = sql();
  const id = nextId("sec");
  await db`INSERT INTO sm_sectors (id, tenant_id, location_id, nombre, orden)
    VALUES (${id}, ${tenantId}, ${input.locationId ?? null}, ${input.nombre.trim()}, ${input.orden ?? 0})`;
  const rows = await db`SELECT * FROM sm_sectors WHERE id = ${id} LIMIT 1`;
  return rowToSector(rows[0]);
}

export async function dbSmPatchSector(
  tenantId: string,
  id: string,
  patch: Partial<{ nombre: string; orden: number; active: boolean }>,
): Promise<Sector | null> {
  const db = sql();
  if (patch.nombre !== undefined)
    await db`UPDATE sm_sectors SET nombre = ${patch.nombre.trim()}, updated_at = NOW() WHERE tenant_id = ${tenantId} AND id = ${id}`;
  if (patch.orden !== undefined)
    await db`UPDATE sm_sectors SET orden = ${patch.orden}, updated_at = NOW() WHERE tenant_id = ${tenantId} AND id = ${id}`;
  if (patch.active !== undefined)
    await db`UPDATE sm_sectors SET active = ${patch.active}, updated_at = NOW() WHERE tenant_id = ${tenantId} AND id = ${id}`;
  const rows = await db`SELECT * FROM sm_sectors WHERE id = ${id} LIMIT 1`;
  return rows[0] ? rowToSector(rows[0]) : null;
}

// ─── Mesas ────────────────────────────────────────────────────────────────────

export async function dbSmListTables(tenantId: string, sectorId?: string): Promise<DineTable[]> {
  const db = sql();
  const rows = sectorId
    ? await db`SELECT * FROM sm_tables WHERE tenant_id = ${tenantId} AND sector_id = ${sectorId} AND active = true ORDER BY nombre`
    : await db`SELECT * FROM sm_tables WHERE tenant_id = ${tenantId} AND active = true ORDER BY nombre`;
  return rows.map(rowToTable);
}

export async function dbSmGetTable(tenantId: string, id: string): Promise<DineTable | null> {
  const db = sql();
  const rows = await db`SELECT * FROM sm_tables WHERE tenant_id = ${tenantId} AND id = ${id} LIMIT 1`;
  return rows[0] ? rowToTable(rows[0]) : null;
}

export async function dbSmGetTableByQrToken(qrToken: string): Promise<DineTable | null> {
  const db = sql();
  const rows = await db`SELECT * FROM sm_tables WHERE qr_token = ${qrToken} AND active = true LIMIT 1`;
  return rows[0] ? rowToTable(rows[0]) : null;
}

/**
 * SCRUM-119: resuelve la sucursal real de una mesa (mesa → sector → sucursal)
 * para propagar `ctx.locationId` a `assertSmStaffPermission` en dine-in.
 * Mesa sin sector o sector sin sucursal asignada → null (nada que restringir,
 * mismo criterio que el resto del gate de location scope).
 */
export async function dbSmGetTableLocationId(tenantId: string, tableId: string): Promise<string | null> {
  const db = sql();
  const rows = await db`
    SELECT sec.location_id AS location_id
    FROM sm_tables t
    JOIN sm_sectors sec ON sec.id = t.sector_id AND sec.tenant_id = t.tenant_id
    WHERE t.tenant_id = ${tenantId} AND t.id = ${tableId}
    LIMIT 1
  `;
  return (rows[0]?.location_id as string | null) ?? null;
}


/**
 * SCRUM-122: sucursal canónica para un pedido de mesa.
 * mesa → sector.location_id; si el sector no tiene sucursal, default activa del tenant.
 */
export async function resolveDineInOrderLocationId(
  tenantId: string,
  tableId: string,
): Promise<string | null> {
  const fromTable = await dbSmGetTableLocationId(tenantId, tableId);
  if (fromTable) return fromTable;
  return resolveDefaultLocationId(tenantId);
}

export async function dbSmCreateTable(
  tenantId: string,
  input: { nombre: string; sectorId?: string | null; capacidad?: number; posX?: number; posY?: number },
): Promise<DineTable> {
  const db = sql();
  const id = nextId("tbl");
  const qrToken = crypto.randomUUID();
  await db`INSERT INTO sm_tables (id, tenant_id, sector_id, nombre, capacidad, pos_x, pos_y, qr_token)
    VALUES (${id}, ${tenantId}, ${input.sectorId ?? null}, ${input.nombre.trim()},
            ${input.capacidad ?? 4}, ${input.posX ?? 0}, ${input.posY ?? 0}, ${qrToken})`;
  return (await dbSmGetTable(tenantId, id))!;
}

/** Regenera qr_token si falta o es inválido (mesas viejas / seed incompleto). */
export async function dbSmEnsureTableQrToken(tenantId: string, tableId: string): Promise<DineTable | null> {
  const table = await dbSmGetTable(tenantId, tableId);
  if (!table) return null;
  const bad =
    !table.qrToken || table.qrToken === "null" || table.qrToken === "undefined" || table.qrToken.length < 8;
  if (!bad) return table;
  const db = sql();
  const qrToken = crypto.randomUUID();
  await db`UPDATE sm_tables SET qr_token = ${qrToken}, updated_at = NOW()
           WHERE tenant_id = ${tenantId} AND id = ${tableId}`;
  return dbSmGetTable(tenantId, tableId);
}

export async function dbSmSetTableStatus(tenantId: string, id: string, estado: TableStatus): Promise<void> {
  const db = sql();
  await db`UPDATE sm_tables SET estado = ${estado}, updated_at = NOW()
           WHERE tenant_id = ${tenantId} AND id = ${id}`;
}

export async function dbSmPatchTable(
  tenantId: string,
  id: string,
  patch: Partial<{
    nombre: string;
    sectorId: string | null;
    capacidad: number;
    posX: number;
    posY: number;
    active: boolean;
    reservable: boolean;
  }>,
): Promise<DineTable | null> {
  const db = sql();
  if (patch.nombre !== undefined)
    await db`UPDATE sm_tables SET nombre = ${patch.nombre.trim()}, updated_at = NOW() WHERE tenant_id = ${tenantId} AND id = ${id}`;
  if (patch.sectorId !== undefined)
    await db`UPDATE sm_tables SET sector_id = ${patch.sectorId}, updated_at = NOW() WHERE tenant_id = ${tenantId} AND id = ${id}`;
  if (patch.capacidad !== undefined)
    await db`UPDATE sm_tables SET capacidad = ${patch.capacidad}, updated_at = NOW() WHERE tenant_id = ${tenantId} AND id = ${id}`;
  if (patch.posX !== undefined)
    await db`UPDATE sm_tables SET pos_x = ${patch.posX}, updated_at = NOW() WHERE tenant_id = ${tenantId} AND id = ${id}`;
  if (patch.posY !== undefined)
    await db`UPDATE sm_tables SET pos_y = ${patch.posY}, updated_at = NOW() WHERE tenant_id = ${tenantId} AND id = ${id}`;
  if (patch.active !== undefined)
    await db`UPDATE sm_tables SET active = ${patch.active}, updated_at = NOW() WHERE tenant_id = ${tenantId} AND id = ${id}`;
  if (patch.reservable !== undefined)
    await db`UPDATE sm_tables SET reservable = ${patch.reservable}, updated_at = NOW() WHERE tenant_id = ${tenantId} AND id = ${id}`;
  return dbSmGetTable(tenantId, id);
}

// ─── Sesiones de mesa ─────────────────────────────────────────────────────────

export async function dbSmGetOpenTableSession(
  tenantId: string,
  tableId: string,
): Promise<TableSession | null> {
  const db = sql();
  const rows = await db`SELECT * FROM sm_table_sessions
                         WHERE tenant_id = ${tenantId} AND table_id = ${tableId} AND status = 'open'
                         ORDER BY opened_at DESC LIMIT 1`;
  return rows[0] ? rowToSession(rows[0]) : null;
}

export async function dbSmGetTableSession(tenantId: string, id: string): Promise<TableSession | null> {
  const db = sql();
  const rows = await db`SELECT * FROM sm_table_sessions WHERE tenant_id = ${tenantId} AND id = ${id} LIMIT 1`;
  return rows[0] ? rowToSession(rows[0]) : null;
}


/** Normaliza id; null si vacío. */
export function normalizeTableSessionId(id?: string | null): string | null {
  const s = typeof id === "string" ? id.trim() : "";
  return s || null;
}

/**
 * Sesión de mesa usable para asociar un pedido.
 * - debe existir en el tenant
 * - preferible abierta; si está cerrada devolvemos null (el caller decide)
 */
export async function dbSmRequireOpenTableSession(
  tenantId: string,
  id: string,
): Promise<TableSession | null> {
  const session = await dbSmGetTableSession(tenantId, id);
  if (!session) return null;
  if (session.status !== "open") return null;
  return session;
}

export async function dbSmOpenTableSession(
  tenantId: string,
  input: { tableId: string; openedBy: string; comensales?: number; notes?: string | null },
): Promise<TableSession> {
  const db = sql();
  const id = nextId("tss");
  await db`INSERT INTO sm_table_sessions (id, tenant_id, table_id, opened_by, comensales, notes)
    VALUES (${id}, ${tenantId}, ${input.tableId}, ${input.openedBy}, ${input.comensales ?? 1}, ${input.notes ?? null})`;
  await dbSmSetTableStatus(tenantId, input.tableId, "OCUPADA");
  return (await dbSmGetTableSession(tenantId, id))!;
}

export async function dbSmCloseTableSession(
  tenantId: string,
  id: string,
  closedBy: string,
): Promise<TableSession | null> {
  const db = sql();
  const session = await dbSmGetTableSession(tenantId, id);
  if (!session) return null;
  await db`UPDATE sm_table_sessions SET
    status = 'closed', closed_at = NOW(), closed_by = ${closedBy}, updated_at = NOW()
    WHERE tenant_id = ${tenantId} AND id = ${id} AND status = 'open'`;
  await dbSmSetTableStatus(tenantId, session.tableId, "LIBRE");
  return dbSmGetTableSession(tenantId, id);
}

export async function dbSmIncrTableSessionTotal(tenantId: string, id: string, amount: number): Promise<void> {
  const db = sql();
  await db`UPDATE sm_table_sessions SET
    total_acumulado = total_acumulado + ${amount}, updated_at = NOW()
    WHERE tenant_id = ${tenantId} AND id = ${id}`;
}
